/**
 * Guest-only auth guard (sign-in / register / forgot-password / etc.).
 *
 * BUG: Signed-in chrome (`session !== null`) can show while SignInScreen
 * still renders because auth routes had no guest-only guard.
 *
 * Behavior:
 *   - Before session hydration / during OIDC land: status only — never
 *     render children (no login form flash).
 *   - When globally signed in (memory `/me` session): replace-navigate to
 *     post-auth return target.
 *   - Otherwise: render guest children.
 *
 * G19 — session authority is CSP memory session after `/me` **200** only.
 * BAN memory JWT / readable access cookie / getToken as signed-in proof
 * under dual|cookie (Spec A mode-agnostic cookie-first). Tenant **and**
 * platform chrome share this rule (half-migration = Fail).
 *
 * G7 SPA ceiling: this is a CSR app — a brief `account.sessionHydrating` status
 * flash is accepted. Server/middleware redirect is out of scope (no SSR).
 *
 * R1 — `oidcLandingPending` uses `window.location.search` as SSOT (same as
 * RequireCustomerAuth), not RR `location.search` alone. Pending = land query
 * only (no readable-token gate — HttpOnly jar has none).
 */

import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useOidcLandGuardPending } from '../../../shared/session/useOidcLandGuardPending.js';
import {
  clearMapResumeParksAfterSoftRaceWin,
  clearPaymentResumeParksAfterSoftRaceWin,
  clearPostAuthNavigateHandoff,
} from '../../../shared/session/returnTarget.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { evaluateGuestOnlyAuth } from './evaluateGuestOnlyAuth.js';
import { resolveGuestAuthRedirectTarget } from './resolveGuestAuthRedirectTarget.js';

interface RequireGuestAuthProps {
  children: ReactNode;
}

function GuestAuthStatus(): JSX.Element {
  const { t } = useTranslation('customer');
  // CSR hydrate status flash is accepted SPA ceiling (no SSR) — brief wait UI only.
  return (
    <p
      className="p-4 text-sm text-[var(--color-on-surface-muted)]"
      role="status"
      data-testid="guest-auth-hydrating"
    >
      {t('account.sessionHydrating')}
    </p>
  );
}

export function RequireGuestAuth({
  children,
}: RequireGuestAuthProps): JSX.Element | null {
  const tenantCode = useTenantCode();
  const { session, sessionHydrated } = useCustomerSession();
  const { oidcLandingPending, oidcLandBudgetExhausted } =
    useOidcLandGuardPending();

  const decision = useMemo(() => {
    // G19 — signed-in = `/me` session DTO only (tenant + platform chrome).
    const globallySignedIn = session !== null;
    return evaluateGuestOnlyAuth({
      sessionHydrated,
      globallySignedIn,
      oidcLandingPending,
      oidcLandBudgetExhausted,
    });
  }, [
    oidcLandingPending,
    oidcLandBudgetExhausted,
    session,
    sessionHydrated,
  ]);

  const redirectTo = useMemo(() => {
    if (!decision.redirectAway) {
      return null;
    }
    return resolveGuestAuthRedirectTarget(tenantCode);
  }, [decision.redirectAway, tenantCode]);

  // G3 / G1 — signed-in Navigate never mounts children, so defense bounce-clear
  // may not run. Peek during render (SoftRace / Strict Mode double-render) via
  // `redirectTo`, then eager clear-after-win after commit (not before peek —
  // that would regress SoftRace park → /browse). Bumps generation so a pending
  // `scheduleClearPostAuthNavigateHandoffAfterSoftRacePeek` from
  // `navigateToPostAuthReturnTarget` is a no-op. Product freeze: no ?tenant=.
  // Payment twin/durable stay readable through SoftRace peek; G3 SSOT clear via
  // `clearPaymentResumeParksAfterSoftRaceWin` after commit so a later /sign-in
  // remount cannot sticky-steal checkout. Navigate timers must not wipe parks.
  useLayoutEffect(() => {
    if (!decision.redirectAway || redirectTo === null) {
      return;
    }
    clearPostAuthNavigateHandoff();
    clearPaymentResumeParksAfterSoftRaceWin(tenantCode, redirectTo);
    clearMapResumeParksAfterSoftRaceWin(tenantCode, redirectTo);
  }, [decision.redirectAway, redirectTo, tenantCode]);

  if (decision.waitHydration) {
    return <GuestAuthStatus />;
  }

  if (decision.redirectAway && redirectTo !== null) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!decision.allowGuestScreen) {
    return <GuestAuthStatus />;
  }

  return <>{children}</>;
}
